import { useEffect, useRef, useState } from 'react'
import api from '../services/api'

interface Suggerimento {
  valore: string
  meta?: Record<string, string> | null
}

interface Props {
  campo: string
  value: string
  onChange: (value: string) => void
  onMetaSelect?: (meta: Record<string, string>) => void
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  disabled?: boolean
}

export default function AutocompleteInput({
  campo, value, onChange, onMetaSelect,
  placeholder, className, style, disabled,
}: Props) {
  const [suggerimenti, setSuggerimenti] = useState<Suggerimento[]>([])
  const [aperto, setAperto] = useState(false)
  const [idx, setIdx] = useState(-1)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    if (!value || value.length < 1) {
      setSuggerimenti([])
      setAperto(false)
      return
    }
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/trasferte/autocomplete/${campo}`, { params: { q: value } })
        setSuggerimenti(res.data || [])
        setAperto(res.data?.length > 0)
        setIdx(-1)
      } catch {
        setSuggerimenti([])
        setAperto(false)
      }
    }, 300)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [value, campo])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setAperto(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const seleziona = (s: Suggerimento) => {
    onChange(s.valore)
    if (s.meta && onMetaSelect) onMetaSelect(s.meta as Record<string, string>)
    setAperto(false)
    setSuggerimenti([])
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!aperto) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIdx(i => Math.min(i + 1, suggerimenti.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && idx >= 0) {
      e.preventDefault()
      seleziona(suggerimenti[idx])
    } else if (e.key === 'Escape') {
      setAperto(false)
    }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <input
        type="text"
        className={className ?? 'input'}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggerimenti.length > 0 && setAperto(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        style={{ width: '100%' }}
      />
      {aperto && suggerimenti.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', margin: 0, padding: 0,
          listStyle: 'none', maxHeight: 220, overflowY: 'auto',
        }}>
          {suggerimenti.map((s, i) => (
            <li
              key={s.valore}
              onMouseDown={() => seleziona(s)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                background: i === idx ? '#EFF6FF' : 'transparent',
                color: '#1E293B',
                borderBottom: i < suggerimenti.length - 1 ? '1px solid #F1F5F9' : 'none',
              }}
            >
              <span style={{ fontWeight: 500 }}>{s.valore}</span>
              {s.meta && Object.keys(s.meta).length > 0 && (
                <span style={{ color: '#94A3B8', fontSize: 11, marginLeft: 8 }}>
                  {Object.values(s.meta).filter(Boolean).slice(0, 2).join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
